<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useRouter, useRoute } from 'vue-router';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { generateURLParams, parseURLParams } from '../helpers/searchHelper';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

import Policy from 'dashboard/components/policy.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SearchHeader from './SearchHeader.vue';
import SearchTabs from './SearchTabs.vue';
import SearchResultConversationsList from './SearchResultConversationsList.vue';
import SearchResultMessagesList from './SearchResultMessagesList.vue';
import SearchResultContactsList from './SearchResultContactsList.vue';
import SearchResultArticlesList from './SearchResultArticlesList.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const PER_PAGE = 15; // Results per page
const selectedTab = ref(route.params.tab || 'all');
const query = ref(route.query.q || '');
const pages = ref({
  contacts: 1,
  conversations: 1,
  messages: 1,
  articles: 1,
});

const contactRecords = useMapGetter('conversationSearch/getContactRecords');
const conversationRecords = useMapGetter(
  'conversationSearch/getConversationRecords'
);
const messageRecords = useMapGetter('conversationSearch/getMessageRecords');
const articleRecords = useMapGetter('conversationSearch/getArticleRecords');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const addTypeToRecords = (records, type) =>
  records.value.map(item => ({ ...useCamelCase(item, { deep: true }), type }));

const mappedContacts = computed(() =>
  addTypeToRecords(contactRecords, 'contact')
);
const mappedConversations = computed(() =>
  addTypeToRecords(conversationRecords, 'conversation')
);
const mappedMessages = computed(() =>
  addTypeToRecords(messageRecords, 'message')
);
const mappedArticles = computed(() =>
  addTypeToRecords(articleRecords, 'article')
);

const isSelectedTabAll = computed(() => selectedTab.value === 'all');

const searchResultSectionClass = computed(() => ({
  'mt-4': isSelectedTabAll.value,
  'mt-0.5': !isSelectedTabAll.value,
}));

const sliceRecordsIfAllTab = items =>
  isSelectedTabAll.value ? items.value.slice(0, 5) : items.value;

const contacts = computed(() => sliceRecordsIfAllTab(mappedContacts));
const conversations = computed(() => sliceRecordsIfAllTab(mappedConversations));
const messages = computed(() => sliceRecordsIfAllTab(mappedMessages));
const articles = computed(() => sliceRecordsIfAllTab(mappedArticles));

const filterByTab = tab =>
  computed(() => selectedTab.value === tab || isSelectedTabAll.value);

const filterContacts = filterByTab('contacts');
const filterConversations = filterByTab('conversations');
const filterMessages = filterByTab('messages');
const filterArticles = filterByTab('articles');

const { shouldShow, isFeatureFlagEnabled } = usePolicy();

const TABS_CONFIG = {
  all: {
    permissions: [
      CONTACT_PERMISSIONS,
      ...ROLES,
      ...CONVERSATION_PERMISSIONS,
      PORTAL_PERMISSIONS,
    ],
    count: () => null, // No count for all tab
  },
  contacts: {
    permissions: [...ROLES, CONTACT_PERMISSIONS],
    count: () => mappedContacts.value.length,
  },
  conversations: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedConversations.value.length,
  },
  messages: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedMessages.value.length,
  },
  articles: {
    permissions: [...ROLES, PORTAL_PERMISSIONS],
    featureFlag: FEATURE_FLAGS.HELP_CENTER,
    count: () => mappedArticles.value.length,
  },
};

const tabs = computed(() => {
  return Object.entries(TABS_CONFIG)
    .map(([key, config]) => ({
      key,
      name: t(`SEARCH.TABS.${key.toUpperCase()}`),
      count: config.count(),
      showBadge: key !== 'all',
      permissions: config.permissions,
      featureFlag: config.featureFlag,
    }))
    .filter(config => {
      // why the double check, glad you asked.
      // Some features are marked as premium features, that means
      // the feature will be visible, but a Paywall will be shown instead
      // this works for pages and routes, but fails for UI elements like search here
      // so we explicitly check if the feature is enabled
      return (
        shouldShow(config.featureFlag, config.permissions, null) &&
        isFeatureFlagEnabled(config.featureFlag)
      );
    });
});

const totalSearchResultsCount = computed(() => {
  const permissionCounts = [
    {
      permissions: [...ROLES, CONTACT_PERMISSIONS],
      count: () => contacts.value.length,
    },
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      count: () => conversations.value.length + messages.value.length,
    },
    {
      permissions: [...ROLES, PORTAL_PERMISSIONS],
      featureFlag: FEATURE_FLAGS.HELP_CENTER,
      count: () => articles.value.length,
    },
  ];

  return permissionCounts
    .filter(config => {
      // why the double check, glad you asked.
      // Some features are marked as premium features, that means
      // the feature will be visible, but a Paywall will be shown instead
      // this works for pages and routes, but fails for UI elements like search here
      // so we explicitly check if the feature is enabled
      return (
        shouldShow(config.featureFlag, config.permissions, null) &&
        isFeatureFlagEnabled(config.featureFlag)
      );
    })
    .map(config => {
      return config.count();
    })
    .reduce((sum, count) => sum + count, 0);
});

const activeTabIndex = computed(() => {
  const index = tabs.value.findIndex(tab => tab.key === selectedTab.value);
  return index >= 0 ? index : 0;
});

const isFetchingAny = computed(() => {
  const { contact, message, conversation, article, isFetching } = uiFlags.value;
  return (
    isFetching ||
    contact.isFetching ||
    message.isFetching ||
    conversation.isFetching ||
    article.isFetching
  );
});

const showEmptySearchResults = computed(
  () =>
    totalSearchResultsCount.value === 0 &&
    uiFlags.value.isSearchCompleted &&
    isSelectedTabAll.value &&
    !isFetchingAny.value &&
    query.value
);

const showResultsSection = computed(
  () =>
    (uiFlags.value.isSearchCompleted && totalSearchResultsCount.value !== 0) ||
    isFetchingAny.value ||
    (!isSelectedTabAll.value && query.value && !isFetchingAny.value)
);

const showLoadMore = computed(() => {
  if (!query.value || isFetchingAny.value || selectedTab.value === 'all')
    return false;

  const records = {
    contacts: mappedContacts.value,
    conversations: mappedConversations.value,
    messages: mappedMessages.value,
    articles: mappedArticles.value,
  }[selectedTab.value];

  return (
    records?.length > 0 &&
    records.length === pages.value[selectedTab.value] * PER_PAGE
  );
});

const showViewMore = computed(() => ({
  // Hide view more button if the number of records is less than 5
  contacts: mappedContacts.value?.length > 5 && isSelectedTabAll.value,
  conversations:
    mappedConversations.value?.length > 5 && isSelectedTabAll.value,
  messages: mappedMessages.value?.length > 5 && isSelectedTabAll.value,
  articles: mappedArticles.value?.length > 5 && isSelectedTabAll.value,
}));

const filters = ref({
  from: null,
  in: null,
  dateRange: { type: null, from: null, to: null },
});

const clearSearchResult = () => {
  pages.value = { contacts: 1, conversations: 1, messages: 1, articles: 1 };
  store.dispatch('conversationSearch/clearSearchResults');
};

const buildSearchPayload = (basePayload = {}, searchType = 'message') => {
  const payload = { ...basePayload };

  // Only include filters if advanced search is enabled
  if (isFeatureFlagEnabled(FEATURE_FLAGS.ADVANCED_SEARCH)) {
    // Date filters apply to all search types
    if (filters.value.dateRange.from) {
      payload.since = filters.value.dateRange.from;
    }
    if (filters.value.dateRange.to) {
      payload.until = filters.value.dateRange.to;
    }

    // Only messages support 'from' and 'inboxId' filters
    if (searchType === 'message') {
      if (filters.value.from) payload.from = filters.value.from;
      if (filters.value.in) payload.inboxId = filters.value.in;
    }
  }

  return payload;
};

const updateURL = () => {
  const params = {
    accountId: route.params.accountId,
    ...(selectedTab.value !== 'all' && { tab: selectedTab.value }),
  };

  const queryParams = {
    ...(query.value?.trim() && { q: query.value.trim() }),
    ...generateURLParams(
      filters.value,
      isFeatureFlagEnabled(FEATURE_FLAGS.ADVANCED_SEARCH)
    ),
  };

  router.replace({ name: 'search', params, query: queryParams });
};

// ─── Aceleres: enviar mensagem para número novo ───────────────────
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const looksLikePhone = computed(() => {
  const digits = (query.value || '').replace(/\D/g, '');
  return digits.length >= 8;
});
const noResults = computed(
  () =>
    uiFlags.value.isSearchCompleted &&
    contacts.value.length === 0 &&
    conversations.value.length === 0
);
const showNewPhoneCta = computed(
  () => query.value && looksLikePhone.value && noResults.value
);
const newConvOpen = ref(false);
const newConvLoading = ref(false);
const newConvInboxes = ref([]);
const newConvInboxId = ref(null);
const newConvMessage = ref('');
function digitsOnly(s) {
  return (s || '').replace(/\D/g, '');
}

async function openNewPhoneModal() {
  newConvOpen.value = true;
  newConvMessage.value = '';
  if (!newConvInboxes.value.length) {
    try {
      const resp = await window.axios.get(
        `/api/v1/accounts/${accountId.value}/inboxes`
      );
      const list = (
        resp.data?.payload ||
        resp.data?.data?.payload ||
        resp.data ||
        []
      ).filter(ib => {
        if (!['Channel::Whatsapp', 'Channel::Api'].includes(ib.channel_type))
          return false;
        if (ib.reauthorization_required === true) return false;
        if (ib.deleted === true || ib.deleted_at) return false;
        const aa = ib.additional_attributes || {};
        const myId = currentUser.value?.id;

        // individual_own: só o owner enxerga a própria
        if (isAceleresIndividualOwn(aa.connection_type)) {
          if (Number(aa.owner_chatwoot_user_id) !== Number(myId)) return false;
        }

        // Channel::Api órfã (sem connection_type) = legacy/lixo
        if (ib.channel_type === 'Channel::Api' && !aa.connection_type)
          return false;

        // Para Channel::Api, exige status connected (sincronizado do Aceleres)
        if (ib.channel_type === 'Channel::Api') {
          if (aa.connection_status && aa.connection_status !== 'connected')
            return false;
        }

        return true;
      });
      newConvInboxes.value = list;
      if (list.length === 1) newConvInboxId.value = list[0].id;
    } catch (e) {
      console.warn('inboxes fetch failed', e);
    }
  }
}
function closeNewPhoneModal() {
  newConvOpen.value = false;
}

async function sendNewPhoneConversation() {
  if (!newConvInboxId.value) {
    alert('Escolha um canal');
    return;
  }
  const msg = (newConvMessage.value || '').trim();
  if (!msg) {
    alert('Digite a mensagem');
    return;
  }
  newConvLoading.value = true;
  try {
    const phoneDigits = digitsOnly(query.value);
    const phoneE164 = phoneDigits.startsWith('55')
      ? `+${phoneDigits}`
      : `+55${phoneDigits}`;

    // 1) Cria (ou recupera) contato
    let contactId = null;
    try {
      const r = await window.axios.post(
        `/api/v1/accounts/${accountId.value}/contacts`,
        {
          name: phoneE164,
          phone_number: phoneE164,
        }
      );
      contactId = r.data?.payload?.contact?.id || r.data?.id;
    } catch (e) {
      // Se já existe, busca pelo phone
      const dup =
        e?.response?.data?.contact?.id ||
        e?.response?.data?.payload?.contact?.id;
      if (dup) {
        contactId = dup;
      } else {
        const search = await window.axios.get(
          `/api/v1/accounts/${accountId.value}/contacts/search?q=${encodeURIComponent(phoneE164)}`
        );
        contactId = search.data?.payload?.[0]?.id;
      }
    }
    if (!contactId) throw new Error('Não conseguiu criar/encontrar contato.');

    // 2) Cria contact_inbox (vincula contato ao inbox e gera source_id)
    const ci = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/contact_inboxes`,
      {
        inbox_id: newConvInboxId.value,
        source_id: phoneE164.replace('+', ''),
      }
    );
    const sourceId = ci.data?.payload?.source_id || phoneE164.replace('+', '');

    // 3) Cria conversa + mensagem inicial
    const conv = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations`,
      {
        source_id: sourceId,
        inbox_id: newConvInboxId.value,
        contact_id: contactId,
        message: { content: msg },
      }
    );
    const convDisplayId = conv.data?.id || conv.data?.payload?.id;
    closeNewPhoneModal();
    router.push({
      name: 'inbox_conversation',
      params: { accountId: accountId.value, conversation_id: convDisplayId },
    });
  } catch (e) {
    console.error(e);
    alert(
      'Erro: ' +
        (e?.response?.data?.message ||
          e?.message ||
          'falha ao iniciar conversa')
    );
  } finally {
    newConvLoading.value = false;
  }
}
// ─── fim Aceleres ───────────────────────────────────────────────

const onSearch = q => {
  query.value = q;
  clearSearchResult();
  updateURL();
  if (!q) return;
  useTrack(CONVERSATION_EVENTS.SEARCH_CONVERSATION);

  const searchPayload = buildSearchPayload({ q, page: 1 });
  store.dispatch('conversationSearch/fullSearch', searchPayload);
};

const onFilterChange = () => {
  onSearch(query.value);
};

const onBack = () => {
  if (window.history.length > 2) {
    router.go(-1);
  } else {
    router.push({ name: 'home' });
  }
  clearSearchResult();
};

const loadMore = () => {
  const SEARCH_ACTIONS = {
    contacts: 'conversationSearch/contactSearch',
    conversations: 'conversationSearch/conversationSearch',
    messages: 'conversationSearch/messageSearch',
    articles: 'conversationSearch/articleSearch',
  };

  if (uiFlags.value.isFetching || selectedTab.value === 'all') return;

  const tab = selectedTab.value;
  pages.value[tab] += 1;

  const payload = buildSearchPayload(
    { q: query.value, page: pages.value[tab] },
    tab
  );

  store.dispatch(SEARCH_ACTIONS[tab], payload);
};

const onTabChange = tab => {
  selectedTab.value = tab;
  updateURL();
};

onMounted(() => {
  store.dispatch('conversationSearch/clearSearchResults');
  store.dispatch('agents/get');

  const parsedFilters = parseURLParams(
    route.query,
    isFeatureFlagEnabled(FEATURE_FLAGS.ADVANCED_SEARCH)
  );
  filters.value = parsedFilters;

  // Auto-execute search if query parameter exists
  if (route.query.q) {
    onSearch(route.query.q);
  }
});

onUnmounted(() => {
  query.value = '';
  store.dispatch('conversationSearch/clearSearchResults');
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1">
    <div
      class="acl-mobile-account-menu-host flex w-full items-center justify-between gap-2 p-4"
    >
      <NextButton
        :label="t('GENERAL_SETTINGS.BACK')"
        icon="i-lucide-chevron-left"
        faded
        primary
        sm
        @click="onBack"
      />
    </div>
    <section class="flex flex-col flex-grow w-full h-full overflow-hidden">
      <div class="w-full max-w-5xl mx-auto z-30">
        <div class="flex flex-col w-full px-4">
          <SearchHeader
            v-model:filters="filters"
            :initial-query="query"
            @search="onSearch"
            @filter-change="onFilterChange"
          />
          <SearchTabs
            v-if="query"
            :tabs="tabs"
            :selected-tab="activeTabIndex"
            @tab-change="onTabChange"
          />
        </div>
      </div>
      <div class="flex-grow w-full h-full overflow-y-auto">
        <div class="w-full max-w-5xl mx-auto px-4 pb-6">
          <div v-if="showResultsSection">
            <Policy
              :permissions="[...ROLES, CONTACT_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultContactsList
                v-if="filterContacts"
                :is-fetching="uiFlags.contact.isFetching"
                :contacts="contacts"
                :query="query"
                :show-title="isSelectedTabAll"
                class="mt-0.5"
              />
              <NextButton
                v-if="showViewMore.contacts"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'contacts'"
              />
            </Policy>

            <Policy
              :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultMessagesList
                v-if="filterMessages"
                :is-fetching="uiFlags.message.isFetching"
                :messages="messages"
                :query="query"
                :show-title="isSelectedTabAll"
                :class="searchResultSectionClass"
              />
              <NextButton
                v-if="showViewMore.messages"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'messages'"
              />
            </Policy>

            <Policy
              :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultConversationsList
                v-if="filterConversations"
                :is-fetching="uiFlags.conversation.isFetching"
                :conversations="conversations"
                :query="query"
                :show-title="isSelectedTabAll"
                :class="searchResultSectionClass"
              />
              <NextButton
                v-if="showViewMore.conversations"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'conversations'"
              />
            </Policy>

            <Policy
              v-if="isFeatureFlagEnabled(FEATURE_FLAGS.HELP_CENTER)"
              :permissions="[...ROLES, PORTAL_PERMISSIONS]"
              :feature-flag="FEATURE_FLAGS.HELP_CENTER"
              class="flex flex-col justify-center"
            >
              <SearchResultArticlesList
                v-if="filterArticles"
                :is-fetching="uiFlags.article.isFetching"
                :articles="articles"
                :query="query"
                :show-title="isSelectedTabAll"
                :class="searchResultSectionClass"
              />
              <NextButton
                v-if="showViewMore.articles"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'articles'"
              />
            </Policy>

            <div v-if="showLoadMore" class="flex justify-center mt-3 mb-6">
              <NextButton
                v-if="!isSelectedTabAll"
                :label="t(`SEARCH.LOAD_MORE`)"
                icon="i-lucide-cloud-download"
                slate
                sm
                faded
                @click="loadMore"
              />
            </div>
          </div>
          <div
            v-else-if="showEmptySearchResults"
            class="flex flex-col items-center justify-center px-4 py-6 mt-8 rounded-md"
          >
            <fluent-icon icon="info" size="16px" class="text-n-slate-11" />
            <p class="m-2 text-center text-n-slate-11">
              {{ t('SEARCH.EMPTY_STATE_FULL', { query }) }}
            </p>
            <!-- Aceleres: CTA novo número -->
            <div v-if="showNewPhoneCta" class="mt-6 w-full max-w-md">
              <div
                class="bg-n-solid-2 border border-n-strong rounded-xl p-6 flex flex-col items-center text-center gap-3"
              >
                <span
                  class="i-lucide-message-circle-plus size-12 text-n-brand"
                ></span>
                <h3 class="text-lg font-semibold text-n-slate-12">
                  Iniciar nova conversa
                </h3>
                <p class="text-sm text-n-slate-11">
                  Quer mandar mensagem para <strong>{{ query }}</strong
                  >?
                </p>
                <button
                  @click="openNewPhoneModal"
                  class="mt-2 px-5 py-2.5 bg-n-brand text-white rounded-lg font-semibold hover:opacity-90 transition"
                >
                  <span
                    class="i-lucide-send size-4 inline-block mr-1.5 align-[-2px]"
                  ></span
                  >Enviar mensagem
                </button>
              </div>
            </div>
          </div>
          <div
            v-else-if="!query"
            class="flex flex-col items-center justify-center px-4 py-6 mt-8 text-center rounded-md"
          >
            <p class="text-center margin-bottom-0">
              <fluent-icon icon="search" size="24px" class="text-n-slate-11" />
            </p>
            <p class="m-2 text-center text-n-slate-11">
              {{ t('SEARCH.EMPTY_STATE_DEFAULT') }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
  <!-- Modal envio rápido -->
  <div
    v-if="newConvOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    @click.self="closeNewPhoneModal"
  >
    <div
      class="bg-n-solid-1 border border-n-strong rounded-xl w-full max-w-md p-6"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-n-slate-12">Nova conversa</h3>
        <button
          @click="closeNewPhoneModal"
          class="text-n-slate-11 text-2xl leading-none"
        >
          ×
        </button>
      </div>
      <div class="space-y-3">
        <div>
          <label class="text-xs text-n-slate-11 font-semibold uppercase"
            >Número</label
          >
          <div class="mt-1 px-3 py-2 bg-n-alpha-2 rounded-lg text-n-slate-12">
            {{ query }}
          </div>
        </div>
        <div>
          <label class="text-xs text-n-slate-11 font-semibold uppercase"
            >Canal</label
          >
          <select
            v-model="newConvInboxId"
            class="mt-1 w-full bg-n-alpha-1 border border-n-strong rounded-lg px-3 py-2 text-n-slate-12"
          >
            <option :value="null" disabled>Escolha um canal…</option>
            <option v-for="ib in newConvInboxes" :key="ib.id" :value="ib.id">
              {{ ib.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="text-xs text-n-slate-11 font-semibold uppercase"
            >Mensagem</label
          >
          <textarea
            v-model="newConvMessage"
            rows="4"
            class="mt-1 w-full bg-n-alpha-1 border border-n-strong rounded-lg px-3 py-2 text-n-slate-12 resize-none"
            placeholder="Digite sua mensagem…"
          ></textarea>
        </div>
      </div>
      <div class="flex gap-2 justify-end mt-5">
        <button
          @click="closeNewPhoneModal"
          class="px-4 py-2 text-n-slate-11 hover:text-n-slate-12"
        >
          Cancelar
        </button>
        <button
          @click="sendNewPhoneConversation"
          :disabled="newConvLoading"
          class="px-5 py-2 bg-n-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {{ newConvLoading ? 'Enviando…' : 'Enviar' }}
        </button>
      </div>
    </div>
  </div>
</template>
